import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import './Signup.css';
import iconBack from '../../assets/images/icon-back.svg';
import iconSearch from '../../assets/images/icon-search.svg';

interface LocationState {
  snsType?: string;
  snsId?: string;
  email?: string;
  name?: string;
  phone?: string;
  carrier?: string;
  gender?: string;
  age?: string;
}

// Daum 우편번호 서비스 타입 선언
declare global {
  interface Window {
    daum: any;
    isFlutterWebView?: boolean;
  }
}

const Location: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const state = routerLocation.state as LocationState;
  const [location, setLocation] = useState('');
  const [, setIsWebView] = useState(true);
  const [showPostcode, setShowPostcode] = useState(false);
  const postcodeRef = useRef<HTMLDivElement>(null);

  // 웹뷰 환경 감지
  useEffect(() => {
    const checkEnvironment = () => {
      console.log('[Location] 환경 감지 시작');

      // localStorage 또는 window 객체에서 Flutter 플래그 체크
      const isFlutter = localStorage.getItem('isFlutterWebView') === 'true' || window.isFlutterWebView === true;

      if (isFlutter) {
        console.log('[Location] ✓ WebView 환경 감지');
        setIsWebView(true);
        return;
      }

      // User Agent 체크
      const userAgent = navigator.userAgent.toLowerCase();
      const isDefinitelyWebView =
        userAgent.includes('wv') ||
        userAgent.includes('bbibbiapp') ||
        userAgent.includes('bbibbi');

      if (isDefinitelyWebView) {
        console.log('[Location] ✓ User Agent로 WebView 감지');
        setIsWebView(true);
        return;
      }

      // 일반 브라우저
      console.log('[Location] ✓ 일반 브라우저 감지');
      setIsWebView(false);
    };

    checkEnvironment();

    const handleFlutterReady = () => {
      console.log('[Location] flutterWebViewReady 이벤트 수신');
      checkEnvironment();
    };

    window.addEventListener('flutterWebViewReady', handleFlutterReady);

    return () => {
      window.removeEventListener('flutterWebViewReady', handleFlutterReady);
    };
  }, []);

  // Daum 우편번호 서비스 스크립트 로드
  useEffect(() => {
    console.log('[Location] Daum 스크립트 로드 시작');
    const script = document.createElement('script');
    script.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // 주소 검색 팝업/모달 열기
  const handleSearchAddress = () => {
    if (!window.daum) {
      alert('주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    setShowPostcode(true);

    // 다음 렌더링 사이클에서 embed
    setTimeout(() => {
      if (postcodeRef.current) {
        new window.daum.Postcode({
          oncomplete: function(data: any) {
            // 시/도 구/군 동까지만 저장
            const sido = data.sido; // 서울특별시
            const sigungu = data.sigungu; // 강남구
            const bname = data.bname; // 역삼동

            const shortAddress = `${sido} ${sigungu} ${bname}`.trim();

            setLocation(shortAddress);
            setShowPostcode(false);
            console.log('[Location] 선택된 주소:', shortAddress);
          },
          width: '100%',
          height: '100%',
        }).embed(postcodeRef.current);
      }
    }, 100);
  };

  // 모달 닫기
  const handleClosePostcode = () => {
    setShowPostcode(false);
  };

  const handleNext = () => {
    if (location) {
      navigate('/signup/nickname', {
        state: {
          ...state,
          location
        }
      });
    }
  };

  const handleSkip = () => {
    navigate('/signup/nickname', { state });
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="signup-container">
      <div className="signup-header">
        <button className="back-button" onClick={handleBack}>
          <img src={iconBack} alt="뒤로가기" />
        </button>
      </div>

      <div className="signup-content">
        <div className="signup-inner">
          <div className="signup-form">
            <div className="signup-title-section">
              <h1 className="signup-title">
                거주중인 지역을
                <br />
                알려 주세요.
              </h1>
            </div>

            <div
              className="location-search"
              onClick={handleSearchAddress}
              style={{ cursor: 'pointer' }}
            >
              <img src={iconSearch} alt="검색" className="search-icon" />
              <input
                type="text"
                className="location-input"
                placeholder="주소를 검색해 주세요."
                value={location}
                readOnly
                style={{ cursor: 'pointer' }}
              />
            </div>

            {location && (
              <div className="selected-location">
                <strong>선택된 지역:</strong> {location}
              </div>
            )}
          </div>

          <div className="signup-footer">
            <div className="spacer"></div>
            <button
              className={`signup-btn ${location ? 'active' : ''}`}
              onClick={handleNext}
              disabled={!location}
            >
              다음
            </button>
            <button className="skip-btn" onClick={handleSkip}>
              건너뛰기
            </button>
          </div>
        </div>
      </div>

      {/* 주소 검색 모달 */}
      {showPostcode && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{
            backgroundColor: '#1e1e1e',
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h2 style={{ color: '#beae79', margin: 0, fontSize: '18px' }}>주소 검색</h2>
            <button
              onClick={handleClosePostcode}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '0 8px',
              }}
            >
              ×
            </button>
          </div>
          <div
            ref={postcodeRef}
            style={{
              flex: 1,
              backgroundColor: 'white',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Location;
